import { Box, Flex, Text } from "@chakra-ui/react"

import { selectAccountCreationStatus, useDappSelector } from "#/redux-state"
import { AccountCreationStatus, ConnectionStatus } from "#/shared/types"

import ConnectWalletStatusIcon from "./ConnectWalletStatusIcon"
import {
  ConnectWalletConnectionStatus,
  ConnectWalletSignatureStatus,
} from "./ConnectWalletStatusText"

type ConnectWalletStatusProps = {
  onWalletConnect?: () => void
  connectionRejected?: boolean
}

const getConnectWalletStatus = (
  isConnecting: boolean,
  isSigning: boolean,
  hasError: boolean,
): ConnectionStatus => {
  if (hasError) {
    return "error"
  }

  if (isSigning) {
    return "done"
  }

  if (isConnecting) {
    return "pending"
  }

  return "idle"
}

const getSignMessageStatus = (
  isSigning: boolean,
  hasError: boolean,
): ConnectionStatus => {
  if (hasError) return "error"

  return isSigning ? "pending" : "idle"
}

export default function ConnectWalletStatus({
  onWalletConnect,
  connectionRejected = false,
}: ConnectWalletStatusProps) {
  const accountCreationStatus = useDappSelector(selectAccountCreationStatus)

  const isConnecting = accountCreationStatus >= AccountCreationStatus.Connecting
  const isSigning = accountCreationStatus >= AccountCreationStatus.Signing

  const hasSigningError =
    accountCreationStatus === AccountCreationStatus.NotSigned

  const connectWalletStatus = getConnectWalletStatus(
    isConnecting,
    isSigning,
    connectionRejected,
  )

  const signMessageStatus = getSignMessageStatus(isSigning, hasSigningError)

  return (
    <Box bg="primary.black.5" p="16px 50px">
      <Text fontWeight={500} fontSize="16px" letterSpacing="0.16px" mb="13px">
        Requires 2 actions:
      </Text>
      <Flex gap="10px" flexDirection="column">
        <Flex alignItems="center" gap="6px">
          <ConnectWalletStatusIcon connectionStatus={connectWalletStatus} />
          <ConnectWalletConnectionStatus
            onClick={onWalletConnect}
            status={connectWalletStatus}
            hasError={connectionRejected}
          />
        </Flex>
        <Flex alignItems="center" gap="6px">
          <ConnectWalletStatusIcon connectionStatus={signMessageStatus} />
          <ConnectWalletSignatureStatus
            status={signMessageStatus}
            hasError={hasSigningError}
          />
        </Flex>
      </Flex>
    </Box>
  )
}
